"use client";
import React, { useRef } from "react";
import * as Sentry from "@sentry/react";
import styles from "./MessageItem.module.scss";
import { formatTimestamp } from "@/app/utils/date";
import Image from "next/image";
import { Message, MessageSender, MessageType } from "@/app/store/types";
import MsgStatus from "./MsgStatus";
import { useMessageActions } from "@/app/hooks/useMessageActions";
import MessageActionMenu from "./MessageActionMenu";
import CollapsibleMessage from "@/app/ui/collapsibleMessage";
import { useMessageRead } from "@/app/hooks/useMessageReaded";
import { getPublicImagePath } from "@/app/utils/image";
import { isForbiddenMsg, isWithdrawnMsg } from "@/app/store/formatMessage";
import Quoted, { QuotedFrom } from "./Quoted";
import Satisfy from "./modules/Satisfy";
import TextUi from "./modules/Text";
import RichTextUi from "./modules/RichText";
import ImageUi from "./modules/Image";
import AudioUi from "./modules/Audio";
import LineUp from "./modules/LineUp";
import VideoUi from "./modules/Video";
import FileUi from "./modules/File";
import MsgEndUi from "./modules/MsgEnd";
import Welcome from "./modules/Welcome";
import Template from "./modules/Template";
import QuestionList from "./modules/QuestionList";
import {
  reporterInstance,
  ReportScene,
  reportScene,
} from "@/app/services/DataReporter";

interface IProps {
  msg: Message;
}

// 为每种消息类型预先定义懒加载组件
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const componentMap: Partial<Record<MessageType, React.ComponentType<any>>> = {
  [MessageType.Text]: TextUi,
  [MessageType.MessageAutoReply]: TextUi,
  [MessageType.IssueType]: TextUi,
  [MessageType.Image]: ImageUi,
  [MessageType.Audio]: AudioUi,
  [MessageType.LineUp]: LineUp,
  [MessageType.Video]: VideoUi,
  [MessageType.File]: FileUi,
  [MessageType.MsgEnd]: MsgEndUi,
  [MessageType.Welcome]: Welcome,
  [MessageType.Template]: Template,
  [MessageType.Satisfy]: Satisfy,
  [MessageType.QuestionList]: QuestionList,
  [MessageType.RichText]: RichTextUi,
  [MessageType.AI]: RichTextUi,
};

const getRenderComponent = (msg: Message): React.ReactNode => {
  const msgType = msg.type as MessageType;
  const LazyComponent =
    componentMap[msgType] || componentMap[MessageType.Text]!;

  return <LazyComponent msg={msg} />;
};

/** 仅开发：`?simulateMessageError=<消息ID>` 与当前条 `msg.id` 一致时抛错，用于验证 ErrorBoundary */
function maybeThrowSimulatedMessageError(msg: Message): void {
  if (process.env.NODE_ENV !== "development" || typeof window === "undefined") {
    return;
  }
  const q = new URLSearchParams(window.location.search).get(
    "simulateMessageError",
  );
  if (!q || msg.id == null || String(msg.id) !== q) {
    return;
  }
  throw new Error(
    `[dev] simulateMessageError=${q}：匹配消息 id（验证 ErrorBoundary）`,
  );
}

/**
 * 仅 ErrorBoundary 兜底：用文本组件按接口数据展示，不使用收起展开，避免再触发复杂子树
 */
function MessageItemErrorFallbackSimple({ msg }: { msg: Message }) {
  const isOwnMessage = msg.senderType === MessageSender.USER;

  const getName = () => {
    switch (msg.senderType) {
      case MessageSender.SYSTEM:
      case MessageSender.AGENT:
        return msg.senderName;
      default:
        return "";
    }
  };

  return (
    <div
      className={`${styles.message} ${styles[isOwnMessage ? "user" : "service"]}`}
    >
      <div className={styles.messageInfo}>
        <div className={styles.messageSender}>{getName()}</div>
        <div className={styles.messageTime}>
          {formatTimestamp(msg.timestamp)}
        </div>
      </div>
      <div className={styles.messageInner}>
        <div className={styles.messageHeader}>
          <div className={styles.messageAvatar}>
            <Image
              src={getPublicImagePath("agentAvatar.svg")}
              alt="agentAvatar"
              width={36}
              height={36}
            />
          </div>
        </div>
        <div className={styles.messageContentContainer}>
          <div className={styles.messageContent} data-msg-type={msg.type}>
            <TextUi msg={msg} />
          </div>
        </div>
      </div>
    </div>
  );
}

// 包含系统消息（排队、结束会话等），人工、用户消息
function MessageItemInner({ msg }: IProps) {
  const isOwnMessage = msg.senderType === MessageSender.USER;
  const menuRef = useRef<HTMLDivElement>(null);
  const {
    showMenu,
    messageRef,
    handleContextMenu,
    handleTouchStart,
    handleTouchEnd,
    handleWithdraw,
    handleQuoted,
    handleCopy,
    loading,
    canWithdraw,
  } = useMessageActions(msg, isOwnMessage, menuRef);

  // 上报用户已读
  const { itemRef } = useMessageRead(msg);

  maybeThrowSimulatedMessageError(msg);

  // 特殊消息类型展示 排队、结束会话、满意度评价
  if (
    [MessageType.MsgEnd, MessageType.LineUp, MessageType.Satisfy].includes(
      msg.type as MessageType,
    )
  ) {
    return getRenderComponent(msg);
  }

  // 特殊消息类型展示 撤回、模版消息
  if (
    [MessageType.Template, MessageType.MessageSatisfyResult].includes(
      msg.type as MessageType,
    ) ||
    isWithdrawnMsg(msg)
  ) {
    if (MessageType.MessageSatisfyResult === msg.type && msg.disabled) {
      return <Satisfy msg={msg} />;
    }
    return <Template msg={msg} />;
  }

  const getName = () => {
    switch (msg.senderType) {
      case MessageSender.SYSTEM:
      case MessageSender.AGENT:
        return msg.senderName;
      default:
        return "";
    }
  };

  return (
    <div
      ref={itemRef}
      className={`${styles.message} ${styles[isOwnMessage ? "user" : "service"]}`}
    >
      <div className={styles.messageInfo}>
        <div className={styles.messageSender}>{getName()}</div>
        <div className={styles.messageTime}>
          {formatTimestamp(msg.timestamp)}
        </div>
      </div>
      {/* 引用消息 */}
      <Quoted
        quotedMsg={msg.quoteMessage as Message}
        msgSender={msg.senderType}
        from={QuotedFrom.Msg}
      />
      <div className={styles.messageInner}>
        <div className={styles.messageHeader}>
          <div className={styles.messageAvatar}>
            <Image
              src={getPublicImagePath("agentAvatar.svg")}
              alt="agentAvatar"
              width={36}
              height={36}
            />
          </div>
        </div>
        <div
          ref={messageRef}
          className={`${styles.messageContentContainer} ${msg.msgClassName ? styles[msg.msgClassName] : ""}`}
          onContextMenu={handleContextMenu}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchEnd}
        >
          {showMenu && (
            <div ref={menuRef}>
              <MessageActionMenu
                sender={msg.senderType}
                onWithdraw={handleWithdraw}
                onQuoted={handleQuoted}
                onCopy={() => handleCopy(msg)}
                loading={loading}
                canWithdraw={canWithdraw}
              />
            </div>
          )}
          <MsgStatus msg={msg} />

          <CollapsibleMessage
            useCollapse={
              msg.senderType !== MessageSender.SYSTEM &&
              msg.type === MessageType.Text
            }
            maxHeight={240}
          >
            <div
              className={`${styles.messageContent} ${[MessageType.Video, MessageType.Audio, MessageType.Image].includes(msg.type as MessageType) && !isForbiddenMsg(msg) ? styles.file : ""}`}
              data-msg-type={msg.type}
            >
              {getRenderComponent(msg)}
            </div>
          </CollapsibleMessage>
        </div>
      </div>
    </div>
  );
}

/** 单条消息隔离：渲染失败时不拖垮整页；兜底仅用 TextUi 直出文本数据，无收起展开 */
export default function MessageItem(props: IProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={<MessageItemErrorFallbackSimple msg={props.msg} />}
      handled
      beforeCapture={(scope) => {
        scope.setTag("errorBoundary", "MessageItem");
        scope.setContext("message", {
          messageId: props.msg.id,
          type: props.msg.type,
        });
      }}
      onError={(error, componentStack, eventId) => {
        reportScene(
          ReportScene.RENDER_ERROR,
          JSON.stringify({
            error: (error as Error)?.message,
            stack: (error as Error)?.stack,
            componentStack,
            eventId,
            tag: "MessageItem",
          }),
        );
        reporterInstance.flush();
      }}
    >
      <MessageItemInner {...props} />
    </Sentry.ErrorBoundary>
  );
}
